import { Stack, Typography, Box } from "@mui/material";
import { Skeleton } from "@mui/material";
import PerformanceItemCard from "./PerformanceItemCard";
import {
  spacing,
  colors,
  typography,
} from "../../../../../../../../assets/styles/tokens";
import {
  formatValue,
  getCurrencySymbol,
  COMPUTED_PERC,
  OutputMetaField,
} from "../../../../../helpers/bizDevelopmentDashboardHelpers";

interface PerformanceItemProps {
  queryOutput: Record<string, any>[];
  comparisonQueryOutput: Record<string, any>[];
  isLoading: boolean;
  isComparisonLoading: boolean;
  isMtd: boolean;
  isComparisonActive: boolean;
  outputMetadata: Record<string, OutputMetaField>;
}

const fmtCurrency = (key: string, value: any): string => {
  const symbol = getCurrencySymbol(key);
  const str = formatValue(value);
  if (str === "N/A") return str;
  return symbol ? `${symbol}${str}` : str;
};

const fmtPerc = (raw: any): string => {
  if (raw === null || raw === undefined) return "N/A";
  const n = parseFloat(String(raw));
  if (isNaN(n)) return "N/A";
  return `${(n * 100).toFixed(1)}%`;
};

// Format a percentage that's already in 0-100 scale (from COMPUTED_PERC)
const fmtComputedPerc = (pct: number | null): string => {
  if (pct === null) return "N/A";
  return `${pct.toFixed(1)}%`;
};

const PerformanceItem = ({
  queryOutput,
  comparisonQueryOutput,
  isLoading,
  isComparisonLoading,
  isMtd,
  isComparisonActive,
  outputMetadata,
}: PerformanceItemProps) => {
  if (isLoading) {
    return (
      <Stack spacing={2} sx={{ mt: spacing.Spacing.xl.$value }}>
        {[0, 1, 2].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={320}
            sx={{ borderRadius: spacing.Radius.lg.$value }}
          />
        ))}
      </Stack>
    );
  }

  if (queryOutput.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "50vh",
        }}
      >
        <Typography
          sx={{
            color: colors.GrayLightMode[500].$value,
            fontSize: typography.Size.Text.md.$value,
          }}
        >
          No data found
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {queryOutput.map((row: Record<string, any>, index: number) => {
        const compRow = isComparisonActive
          ? comparisonQueryOutput.find((r) => r.brand_id === row.brand_id)
          : undefined;

        // CPA% and RS% — computed as (value / total) * 100, same as desktop COMPUTED_PERC
        const cpaPercComputed = COMPUTED_PERC.cpa_income_usd(row);
        const rsPercComputed = COMPUTED_PERC.revshare_income_usd(row);

        return (
          <PerformanceItemCard
            key={index}
            website_name={String(row.brand_display_name ?? "")}
            website_id={String(row.brand_id ?? "")}
            totalIncome={fmtCurrency("total_income_usd", row.total_income_usd)}
            cpa={fmtCurrency("cpa_income_usd", row.cpa_income_usd)}
            cpaPercentage={fmtComputedPerc(cpaPercComputed)}
            rs={fmtCurrency("revshare_income_usd", row.revshare_income_usd)}
            rsPercentage={fmtComputedPerc(rsPercComputed)}
            reg={formatValue(row.reg)}
            clicks={formatValue(row.clicks)}
            ftd={formatValue(row.ftd)}
            ftd_percentage={fmtPerc(row.ftd_perc)}
            qftd={formatValue(row.qftd)}
            qftd_percentage={fmtPerc(row.qftd_perc)}
            ftd_incent={formatValue(row.ftd_incent)}
            ftd_incent_percentage={fmtPerc(row.ftd_incent_perc)}
            rawRow={row}
            isMtd={isMtd}
            isComparisonActive={isComparisonActive}
            isComparisonLoading={isComparisonLoading}
            comparisonRow={compRow}
            outputMetadata={outputMetadata}
          />
        );
      })}
    </Stack>
  );
};

export default PerformanceItem;
